import React from 'react';
import { SEO } from '../components/seo/SEO';
import { client } from '../lib/sanity';
import type { Pet } from '../types/sanity';
import { RainbowBridgeHero } from '../components/rainbowBridge/RainbowBridgeHero';
import { DeceasedPetCard } from '../components/rainbowBridge/DeceasedPetCard';
import { RainbowBridgeQuote } from '../components/rainbowBridge/RainbowBridgeQuote';
import { getBreadcrumbSchema } from '../lib/jsonLd';

export function RainbowBridgePage() {
  const [pets, setPets] = React.useState<Pet[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Strona główna', url: 'https://znajdki.pl/' },
    { name: 'Za tęczowym mostem', url: 'https://znajdki.pl/za-teczowym-mostem' }
  ]);

  React.useEffect(() => {
    const fetchDeceasedPets = async () => {
      try {
        const result = await client.fetch<Pet[]>(`
          *[_type == "pet" && status == "deceased" && !(_id in path("drafts.**"))] | order(coalesce(deceasedDate, _updatedAt) desc) {
            _id,
            name,
            birthDate,
            deceasedDate,
            deceasedDescription,
            image,
            description,
            gender,
            size,
            "tags": tags[]-> {
              _type,
              _ref,
              label,
              "slug": slug
            },
            mainCategory
          }
        `);

        setPets(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching deceased pets:', err);
        setError('Nie udało się załadować danych');
        setLoading(false);
      }
    };

    fetchDeceasedPets();
  }, []);

  const calculateAge = (birthDate: string, deceasedDate: string) => {
    try {
      const birth = new Date(birthDate);
      const deceased = new Date(deceasedDate);
      
      const ageInMonths = (deceased.getFullYear() - birth.getFullYear()) * 12 + 
        (deceased.getMonth() - birth.getMonth());
      
      if (ageInMonths < 12) {
        return `${ageInMonths} mies.`;
      }
      
      const age = Math.floor(ageInMonths / 12);
      return `${age} ${age === 1 ? 'rok' : age < 5 ? 'lata' : 'lat'}`;
    } catch (error) {
      console.error('Error calculating age:', error);
      return 'Nieznany wiek';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <SEO 
        title="Za tęczowym mostem - Pamięć o podopiecznych"
        description="Wspomnienie o naszych podopiecznych, którzy odeszli. Pamiętamy o każdym z nich - psy i koty, które żyły szczęśliwie dzięki Fundacji Znajdki."
        url="/za-teczowym-mostem"
        image="/images/animals/fundacja-znajdki-zwierzeta-001.webp"
        jsonLd={breadcrumbSchema}
      />

      <RainbowBridgeHero />

      <RainbowBridgeQuote />

      {loading ? (
        <div className="text-center py-12">
          <p className="body-large">Ładowanie...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="body-large text-accessible-red-600">{error}</p>
        </div>
      ) : pets.length === 0 ? (
        <div className="text-center py-12">
          <p className="body-large">Brak zwierząt w tej sekcji.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {pets.map((pet) => (
            <DeceasedPetCard
              key={pet._id}
              pet={pet}
              calculateAge={(birthDate) => {
                if (!birthDate) return "Nieznany wiek";
                return pet.deceasedDate 
                  ? calculateAge(birthDate, pet.deceasedDate) 
                  : "Nieznany wiek";
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}